/**
 * Legacy enumerations, catalogues and business rules.
 *
 * This module imports NOTHING — same discipline as api/src/read/sql.ts, and
 * for the same reason: these are the functions that decide what a customer
 * is charged, so they must be executable by a harness that runs before any
 * dependency is installed (see scripts/verify-rules.mjs).
 *
 * index.ts builds the Zod enums from the tuples exported here, so a value
 * added in one place cannot go missing in the other.
 */
/* ------------------------------------------------------------------ *
 * Enumerations (values are the literal strings stored in MySQL)
 * ------------------------------------------------------------------ */
/**
 * `user.status` is the ROLE column. It is not an "is active" flag.
 * `deleted` and `disabled` are the two states that must never log in.
 */
export const USER_ROLES = [
    'admin',
    'manager',
    'operator',
    'driver',
    'partner',
    'disabled',
    'deleted',
];
export const LOGIN_BLOCKED_ROLES = ['disabled', 'deleted'];
export const RESERVATION_STATUSES = [
    'new',
    'verified',
    'paid',
    'closed',
    'canceled',
    'noshow',
    'deleted',
];
/**
 * Parking product. The pricelist column that applies is NOT the same name:
 *   cheap     -> pricelist.cheap_N
 *   regular   -> pricelist.outside_N
 *   exclusive -> pricelist.inside_N
 * See PRICELIST_COLUMN below — getting this wrong silently misprices.
 */
export const RESERVATION_TYPES = ['cheap', 'regular', 'exclusive'];
export const PRICELIST_COLUMN = {
    cheap: 'cheap',
    regular: 'outside',
    exclusive: 'inside',
};
export const PAYMENT_METHODS = ['card', 'cash', 'go_pay', 'bitpay', 'invoice'];
/** `club_user.level`. Silver is the default tier, so only gold/platinum
 *  earn a badge in the UI — flagging 66k silver rows would be noise. */
export const CLUB_TIERS = ['silver', 'gold', 'platinum'];
/** `reservation.parking_location` — which site the car is standing on. */
export const PARKING_LOCATIONS = ['airport', 'tuchomerice'];
export const TRANSFER_DIRECTIONS = ['e2t', 't2e'];
/**
 * The highest page number any listing will answer.
 *
 * `page` had a minimum and no maximum, so `?page=999999999` was a legal
 * request. Every list query is `LIMIT ? OFFSET ?`, and an offset is not free:
 * the server walks and discards every row before it. On the reservation grid
 * that is a full scan of a 300 MB table, returning nothing, held open for as
 * long as it takes — from one signed-in session, with no search term and no
 * date range, as fast as the caller cares to repeat it.
 *
 * Twenty thousand is chosen against the real tables rather than picked for
 * roundness: the largest list is 97,902 reservations in staging, and the
 * grid's default page size is 50, so the last real page is 1,959. Even at the
 * smallest page size the schemas allow, this bound sits an order of magnitude
 * past the end of the data. An operator cannot reach it. A loop can, and is
 * then stopped by validation instead of by the database.
 *
 * It is a bound on the symptom, not a fix for the cost: offset paging gets
 * slower the deeper it goes whatever the ceiling. When writes arrive and the
 * grids grow, the fix is keyset paging — remember the last row's sort key and
 * ask for what follows it — which has no offset to bound at all.
 */
export const MAX_PAGE = 20_000;
/**
 * The columns the Rezervace grid can be ordered by — one per clickable
 * header of the legacy table (page.reservations.html), in its order: ID,
 * Stav, Klient, Partner, Ceník, Kde (the product), Příjezd, Odjezd, Dní,
 * Rezervováno, Cena, GoPay, Fakturace CK, Uhradit. `price` (the bare parking
 * price) stays for Statistiky and old links; the grid's Cena is `total`, the
 * legacy `price_total` with services and the late penalty in it.
 */
export const RESERVATION_SORTS = [
    'id',
    'status',
    'driverName',
    'partner',
    'pricelist',
    'type',
    'arrival',
    'departure',
    'days',
    'created',
    'total',
    'gopay',
    'invoice',
    'topay',
    'price',
];
/**
 * The legacy Stav filter, exactly the option list of page.reservations.html
 * and the WHERE each option produced in page.reservations.php:
 *
 *   active     "Živé"                                  new, pending, verified, paid
 *   waiting    "Příjezdy"                              new, pending, paid
 *   notdeleted "Bez storen, smazaných a propadlých"    NOT IN canceled, deleted, noshow
 *   <status>   "Pouze: …"                              that one status
 *
 * No option at all is no clause at all — the old table shows deleted rows
 * too, greyed out, and its "Celkem: N rezervací" counts them. Staff read that
 * number against ours, so ours has to count the same rows.
 *
 * `pending` is in the IN lists because the legacy lists have it, although no
 * row in thirteen years of data carries it; it is not in RESERVATION_STATUSES
 * for the same reason.
 */
export const RESERVATION_STATUS_GROUPS = ['active', 'waiting', 'notdeleted'];
export const RESERVATION_STATUS_FILTERS = [...RESERVATION_STATUS_GROUPS, ...RESERVATION_STATUSES];
export const RESERVATION_STATUS_GROUP_MEMBERS = {
    active: { in: ['new', 'pending', 'verified', 'paid'] },
    waiting: { in: ['new', 'pending', 'paid'] },
    notdeleted: { notIn: ['canceled', 'deleted', 'noshow'] },
};
/** The legacy Smile Club filter (page.reservations.php FILTER smileclub). */
export const CLUB_FILTERS = ['member', 'notmember', 'notactive', 'silver', 'gold', 'platinum'];
/**
 * The legacy "Web (zdroj)" filter. Each value is the WHERE the old page
 * built, reproduced in buildReservationWhere; `dovolena` is list #43 or the
 * promo code, `parkvia` is the pricelist's title, `ck` is the account's role.
 */
export const SOURCE_FILTERS = ['smile', 'dovolena', 'valet', 'parkvia', 'ck'];
export const DOVOLENA_PRICELIST_ID = 43;
/** The legacy list page: DEFAULT_ITEMS_PER_PAGE. */
export const RESERVATION_PER_PAGE = 20;
/** The legacy default window when the date box is empty: getInterval('last3months') on `created`. */
export const RESERVATION_LOOKBACK_MONTHS = 3;
/**
 * Sortable columns on the Klienti (Smile Parking Club) list.
 *
 * Every entry here is a physical column of `club_user`. The points columns
 * are deliberately absent: they are aggregated per page from three side
 * tables, so ordering the whole 67k-row member list by them would mean
 * grouping 102k point rows and 23k redemptions on every keystroke — which is
 * exactly what the legacy screen does, and why it is slow. The screen labels
 * the point columns as unsortable rather than offering a control that would
 * sort only the visible page and quietly lie about the rest.
 */
export const CLIENT_SORTS = ['id', 'email', 'registration', 'level', 'status'];
/**
 * `club_user.status` — membership state, not a role. The legacy vocabulary is
 * kept because it is what the old screen prints and what staff say out loud.
 */
export const CLIENT_STATUSES = ['ok', 'disabled', 'deleted'];
/**
 * What the Klienti toolbar's `Stav` control may be set to: the three stored
 * states plus the two derived ones the legacy screen offers. 'pending' and
 * 'active' are not columns — they are `status = 'ok'` split by whether the
 * member has ever signed in to the customer portal.
 *
 * Derived from CLIENT_STATUSES rather than written out, because this list has
 * to be the same on both sides of the wire: the API validates against it and
 * the browser now rejects an unrecognised `?stav=` before sending it. Two
 * hand-maintained copies of the same five strings is how a screen ends up
 * offering a filter the server refuses.
 */
export const CLIENT_STATUS_FILTERS = [...CLIENT_STATUSES, 'pending', 'active'];
/**
 * Which of a member's profiles stands for them on screen: the preferred one,
 * unless it has been deleted, and otherwise the newest surviving one.
 *
 * A member keeps a profile per booking identity — a private car, a company
 * car, a spouse's plate — and may delete any of them. `prefered_id` points at
 * one, and the old system does not clear it when that one goes.
 *
 * The deletion filter therefore runs *first*. Asking for the preferred
 * profile first and treating "not deleted" as a fallback reads the same and
 * behaves differently: the preferred branch answers before anything has
 * checked whether it was deleted, so the member who deleted the profile the
 * database still flags keeps that name and that licence plate on the list.
 * Three members on staging are in exactly that state.
 *
 * A member whose profiles have all been deleted gets none, and the screen
 * shows an empty cell. Printing a name somebody explicitly removed is the
 * worse of the two answers.
 *
 * Structurally typed, and it lives here rather than beside the read model,
 * so the harness can execute this exact function instead of a copy of it.
 * The caller passes profiles newest-first, which is what `usable[0]` means.
 */
export function displayProfile(profiles) {
    const usable = profiles.filter((p) => p.status !== 'deleted');
    return usable.find((p) => p.preferred) ?? usable[0] ?? null;
}
/* ------------------------------------------------------------------ *
 * Parking catalogues
 * ------------------------------------------------------------------ */
/** Europort floors. Legacy bug: 9..13 all shipped value="8" in the form. */
export const EUROPORT_SPOTS = [
    // The legacy `$parking_cs` words, exactly: 'Venku u vjezdu', 'M. patro',
    // 'Venku u výjezdu'. The rule is the staff are not being retrained.
    { value: 'outside_entry', label: 'Venku u vjezdu' },
    { value: 'M', label: 'M. patro' },
    { value: '7', label: '7. patro' },
    { value: '8', label: '8. patro' },
    { value: '9', label: '9. patro' },
    { value: '10', label: '10. patro' },
    { value: '11', label: '11. patro' },
    { value: '12', label: '12. patro' },
    { value: '13', label: '13. patro' },
    { value: 'outside_exit', label: 'Venku u výjezdu' },
];
export const TUCHOMERICE_SPOTS = [
    'Hala 1', 'Hala 2',
    'Tráva A', 'Tráva B', 'Tráva C', 'Tráva D', 'Tráva E', 'Tráva F',
    'Šterk', 'Náměstí', 'Pod halou', 'Za halou',
    'Před POPem 1', 'Před POPem 2', 'Před POPem 3', 'Před POPem 4',
    'POP A', 'POP B', 'POP C', 'POP D', 'POP E', 'POP F', 'POP G',
    'POP 1', 'POP 2', 'POP 3', 'POP 4', 'POP 5', 'POP 6', 'POP 7', 'POP 8',
    'Šestka 1',
    'Kněževes K1', 'Kněževes K2', 'Kněževes K3', 'Kněževes K4',
    'Kněževes K5', 'Kněževes K6', 'Kněževes K7', 'Kněževes K8',
];
/* ------------------------------------------------------------------ *
 * Legacy business rules — single source of truth
 * ------------------------------------------------------------------ */
/**
 * Billable days, exactly as the legacy PHP computes it.
 *
 *   days = floor(((departure - 1801s) - arrival) / 86400) + 1
 *
 * The 1801-second grace means a stay that runs 30 minutes past the hour
 * does not tip into an extra day. Reproduced verbatim — do NOT "clean up".
 */
export function billableDays(arrival, departure) {
    const secs = (departure.getTime() - 1801_000 - arrival.getTime()) / 1000;
    return Math.floor(secs / 86400) + 1;
}
/**
 * Price for N days from a pricelist row. Columns are col_1..col_31; from
 * day 32 onward the legacy rule is col_31 + col_32 * (days - 31).
 *
 * NOTE: this is for NEW quotes only. Existing reservations carry a locked
 * `price` and must never be recomputed — historical pricelists were edited
 * in place, so recomputation produces figures that were never charged.
 */
export function priceForDays(cols, days) {
    if (days <= 0)
        return 0;
    if (days <= 31)
        return cols[days] ?? 0;
    const base = cols[31] ?? 0;
    const extra = cols[32] ?? 0;
    return base + extra * (days - 31);
}
/**
 * The late-departure penalty, exactly as the legacy SQL computed it on
 * every read (`price_penalty` in class.Reservation.php):
 *
 *   late = minutes(departure → departure_real, or now while the car is here)
 *   penalty = late > 360 ? (days(departure → real − 180 min) + 1) × 800 : 0
 *
 * where days() is MySQL's TIMESTAMPDIFF(DAY): whole days, floored. Six
 * hours of grace, then 800 Kč a started day, counted from three hours
 * after the planned departure. Not stored anywhere — the legacy recomputed
 * it on every screen, and so does this one.
 */
export function latePenalty(departure, realOrNow) {
    const lateMin = (realOrNow.getTime() - departure.getTime()) / 60_000;
    if (!(lateMin > 360))
        return 0;
    const days = Math.floor((realOrNow.getTime() - 180 * 60_000 - departure.getTime()) / 86_400_000);
    return (days + 1) * 800;
}
/** Amount already collected across all four legacy payment columns. */
export function paidTotal(r) {
    return (Number(r.normal_paid ?? 0) +
        Number(r.gopay_paid ?? 0) +
        Number(r.bitpay_paid ?? 0) +
        Number(r.invoice_paid ?? 0));
}
/** Shift boundaries: day 08:00–20:00, night 20:00–08:00. */
export function shiftFor(at) {
    const h = at.getHours();
    const d = new Date(at);
    d.setMinutes(0, 0, 0);
    if (h >= 8 && h < 20) {
        const from = new Date(d);
        from.setHours(8);
        const to = new Date(d);
        to.setHours(20);
        return { kind: 'day', from, to };
    }
    const from = new Date(d);
    if (h < 8)
        from.setDate(from.getDate() - 1);
    from.setHours(20);
    const to = new Date(from);
    to.setDate(to.getDate() + 1);
    to.setHours(8);
    return { kind: 'night', from, to };
}
/**
 * The shift `offset` shifts after the one `at` falls in. 0 is the current
 * shift, 1 the next, and so on — the dashboard's "Další směny" view.
 *
 * Stepped in LOCAL hours, not milliseconds: shifts are twelve hours long on
 * the rota, but twice a year one of them is eleven or thirteen hours long on
 * the clock, and 12 × offset milliseconds added on that day lands at 19:00 or
 * 21:00 — one of which `shiftFor` reads as the wrong shift. `setHours` lets
 * the Date arithmetic absorb the change, so the walk stays on the rota.
 */
export function shiftAt(at, offset) {
    const n = Math.max(0, Math.floor(offset));
    if (n === 0)
        return shiftFor(at);
    const cur = shiftFor(at);
    const probe = new Date(cur.from);
    probe.setHours(probe.getHours() + 12 * n + 1);
    return shiftFor(probe);
}
export function legacyShiftAt(now, balanceStart) {
    const at = (h, dayOffset = 0) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset, h, 0, 0, 0);
    const t02 = at(2);
    const t14 = at(14);
    const bs = balanceStart ?? null;
    let kind;
    let from;
    let till;
    if (bs && bs.getTime() > t02.getTime() && bs.getTime() <= t14.getTime()) {
        kind = 'day';
        from = at(8);
        till = at(20);
    }
    else {
        kind = 'night';
        if (!bs || bs.getTime() <= t14.getTime()) {
            from = at(20, -1);
            till = at(8);
        }
        else {
            from = at(20);
            till = at(8, 1);
        }
    }
    const fromBalance = bs ?? from;
    const plus12 = new Date(fromBalance.getTime());
    plus12.setHours(plus12.getHours() + 12);
    let tillOverlap = plus12.getTime() > till.getTime() ? plus12 : till;
    if (till.getTime() < now.getTime())
        tillOverlap = new Date(now.getTime() + 2 * 3600_000);
    return { kind, from, till, fromBalance, tillOverlap };
}
/**
 * Which rota slot a shift that has already been recorded belongs to, from the
 * hour it began.
 *
 * This is NOT `shiftFor`, and the difference matters enough to be worth two
 * paragraphs. `shiftFor` answers "which shift is it right now", against the
 * nominal rota of 08:00 and 20:00. That is the right question for the
 * dashboard and the wrong one for a row in the `balance` table, because the
 * rota is 08:00 and 20:00 but the data is not: the two most common start
 * hours across thirteen years are 19:00 (2,073 shifts) and 07:00 (1,913),
 * because the arriving operator opens the till before the outgoing one
 * leaves. Ask `shiftFor` about a shift that began at 19:04 and it says "day",
 * because 19:04 is inside the day window — and a night's takings get filed
 * under the day.
 *
 * So the rule here is proximity to an anchor rather than membership of a
 * window: anything within four hours of 08:00 is the day shift, anything
 * within four hours of 20:00 is the night one. On the staging data that gives
 * 4,649 day, 4,550 night and 118 'other', summing exactly to the 9,317 rows in
 * the table.
 *
 * The 118 are the reason there is a third value. They are shifts that began at
 * 02:00 or 14:00 — splits, restarts, a till reopened after a mistake — and
 * they are real money. Forcing them into the nearer slot would be tidier and
 * would put a night manager in front of the day's takings believing them to be
 * his own. 'Mimo rozpis' is a less comfortable label and a true one.
 */
export function shiftKindOf(started) {
    if (!started || started.length < 13)
        return 'other';
    const h = Number(started.slice(11, 13));
    if (!Number.isInteger(h) || h < 0 || h > 23)
        return 'other';
    if (h >= 4 && h <= 12)
        return 'day';
    if (h >= 16 || h === 0)
        return 'night';
    return 'other';
}
/* ---- balance.closed is stored in UTC. balance.started is not. ----- *
 *
 * This is a legacy defect, not a design choice, and it is worth stating
 * plainly because it moves money between shifts.
 *
 * In the `balance` table, `started` holds Prague wall-clock time and `closed`
 * holds the same kind of string but in UTC. The evidence is not circumstantial:
 * across all 9,316 consecutive pairs of shifts in the staging database, adding
 * the Prague offset to `closed` reproduces the next shift's `started` exactly,
 * to the second, in both winter (+1) and summer (+2). Corrected, the shifts
 * come out twelve hours long, which is what the rota actually is; uncorrected,
 * every shift looks one or two hours short.
 *
 * The consequence, measured on July 2026: 420 of 2,212 cars and 30,700 CZK of
 * 231,364 — 13.3% of the takings — depart inside the window the stored
 * timestamp leaves out. An uzávěrka built on the raw value would hand the
 * night shift a couple of hours of the day shift's cash and no one would be
 * able to explain the difference.
 *
 * The offset is computed for Europe/Prague explicitly rather than from the
 * server's own clock, because the API runs in a container whose timezone is
 * whatever the image happened to ship with, and being right only on a laptop
 * set to Prague is not being right.
 */
/** Minutes Europe/Prague is ahead of UTC at a given instant (+60 or +120). */
export function pragueOffsetMinutes(at) {
    const f = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Prague',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const p = {};
    for (const part of f.formatToParts(at)) {
        if (part.type !== 'literal')
            p[part.type] = part.value;
    }
    // 'en-GB' renders midnight as 24, which Date.UTC would roll into the next
    // day and turn a +120 offset into -1320.
    const hour = Number(p.hour) % 24;
    const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
    return Math.round((asUtc - at.getTime()) / 60_000);
}
/**
 * The true local end of a shift, from the UTC value the legacy app stored.
 *
 * Takes and returns the same 'YYYY-MM-DD HH:MM:SS' shape the rest of the read
 * model passes around, so the corrected bound can go straight into a query
 * parameter beside `started`, which needs no correction at all.
 */
export function closedLocal(closedUtc) {
    if (!closedUtc || closedUtc.length < 19)
        return closedUtc;
    const at = new Date(`${closedUtc.replace(' ', 'T')}Z`);
    if (!Number.isFinite(at.getTime()))
        return closedUtc;
    const local = new Date(at.getTime() + pragueOffsetMinutes(at) * 60_000);
    const p = (n) => String(n).padStart(2, '0');
    return (`${local.getUTCFullYear()}-${p(local.getUTCMonth() + 1)}-${p(local.getUTCDate())} ` +
        `${p(local.getUTCHours())}:${p(local.getUTCMinutes())}:${p(local.getUTCSeconds())}`);
}
/* ------------------------------------------------------------------ *
 * Statistiky: chart granularity
 * ------------------------------------------------------------------ */
/**
 * A day bucket is only legible up to roughly a quarter's worth of bars. Past
 * that the series switches to months. The number lives in shared code rather
 * than in the chart so the API and the axis caption can never disagree about
 * which one was served.
 */
export const STATS_DAY_MAX_SPAN = 92;
/**
 * Days between two 'YYYY-MM-DD' strings, inclusive of both ends.
 *
 * This is plain JavaScript on two calendar dates, not the SQL day difference
 * the stay-length breakdown would have needed — see the note at the top of the
 * Statistiky section in api/src/read/sql.ts for why that one does not exist.
 */
function spanDays(from, to) {
    const a = Date.parse(`${from}T00:00:00Z`);
    const b = Date.parse(`${to}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b))
        return Number.POSITIVE_INFINITY;
    return Math.floor((b - a) / 86_400_000) + 1;
}
/**
 * Resolve 'auto' to a real granularity.
 *
 * An unbounded range means months: the staging data alone spans 2021 to 2027,
 * and 2,200 day-bars is not a chart. A bounded range gets days while it still
 * fits, which covers the two filters staff actually use — this month, and a
 * quarter.
 *
 * It lives here, in the module that imports nothing, for the same reason
 * billableDays() does: it decides what a manager is shown, and the harness has
 * to be able to execute the real one rather than a copy that agrees with
 * itself.
 *
 * BOTH bounds are required for days, and that is the correction of a real
 * defect. An earlier version substituted today for a missing `to` — but the
 * WHERE clause the same query builds does not, so clearing the second date box
 * left the guard measuring a one-day span while the query returned every
 * future booking on file and the chart drew a hundred and forty-eight bars.
 * The rule only holds if the span it measures is the span the query asks for,
 * so an open end is what it is: unbounded, therefore months. A manager who
 * wants an open range by days can still say so — that is what the granularity
 * select is for, and an explicit choice is never second-guessed here.
 */
export function resolveStatsUnit(unit, from, to) {
    if (unit !== 'auto')
        return unit;
    if (!from || !to)
        return 'month';
    return spanDays(from, to) <= STATS_DAY_MAX_SPAN ? 'day' : 'month';
}
